import React, { useState, useEffect } from "react";
import {
    Row, Col, Button, Input, Spinner,
    Modal, ModalHeader, ModalBody, ModalFooter, Label, FormGroup,
} from "reactstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../../api/urls";
import { CATEGORY_META, CATEGORY_OPTIONS, PRIORITY_META, PRIORITY_OPTIONS } from "./ticketMeta";
import AttachmentPicker from "./AttachmentPicker";
import { useTicketAttachments } from "./useTicketAttachments";
import { buildMessagePayload } from "./buildMessagePayload";
import { useAuth } from "../../hooks/useAuth";

const NAVY = "#221C5D";
// Backend spam koruması bu durum koduyla yanıt verir (RateLimiterService).
const TOO_MANY_REQUESTS = 429;
// Talep sahibi seçimi tek sayfada listelenir; firma başına kullanıcı sayısı bunun altındadır.
const USER_OPTIONS_LIMIT = 200;

// Kategori varsayılanı CATEGORY_OPTIONS'ın ilk elemanıdır: sabit bir değer yazılırsa
// (eskiden "GENERAL" yazıyordu) enum'dan kaldırıldığı gün form geçersiz
// kategori göndermeye başlar — açılır listede seçili görünen değerle gönderilen değer
// birbirinden ayrılmasın diye tek kaynak burası.
const DEFAULT_FORM = {
    subject: "",
    category: CATEGORY_OPTIONS[0],
    priority: "MEDIUM",
    message: "",
};

/**
 * "Yeni Destek Talebi" formu — hem Destek Taleplerim hem Destek Yönetimi ekranından açılır.
 *
 * Talep oluşturma kuralları (adına açma, ek yükleme, spam limiti) tek yerde dursun diye
 * bileşen olarak çıkarıldı; iki ekran da aynı gövdeyi gönderir.
 *
 * @param isOpen     modal açık mı
 * @param toggle     kapatma isteği (Vazgeç / dış tıklama)
 * @param onCreated  başarıyla oluşturulunca çağrılır; parametre yeni talebin id'si
 *                   (yönlendirmeyi çağıran ekran kendi rotasına göre yapar)
 * @param initialValues modal açılırken forma yazılacak alanlar (ör. paket teklifi konusu)
 */
export default function NewTicketModal({ isOpen, toggle, onCreated, initialValues }) {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(DEFAULT_FORM);

    useEffect(() => {
        if (isOpen && initialValues) setForm({ ...DEFAULT_FORM, ...initialValues });
    }, [isOpen]);
    const attachments = useTicketAttachments(toast.error);

    // Destek personeli, telefon/başka kanaldan iletilen talebi müşteri adına açabilir.
    // Alanlar yalnız bu rolde render edilir; diğer rollerde form birebir eskisi gibi kalır.
    const { roles } = useAuth();
    const isSuperAdmin = roles.includes("ROLE_SUPER_ADMIN");
    const [companies, setCompanies] = useState([]);
    const [companyUsers, setCompanyUsers] = useState([]);
    const [onBehalf, setOnBehalf] = useState({ companyId: "", userId: "" });

    // Firma listesi modal ilk açıldığında bir kez yüklenir; diğer rollerde hiç istek atılmaz.
    useEffect(() => {
        if (!isOpen || !isSuperAdmin || companies.length > 0) return;
        axios.get(API.get_companies_names)
            .then(({ data }) => { if (data.statusCode === 200) setCompanies(data.data || []); })
            .catch(() => toast.error("Firma listesi yüklenemedi"));
    }, [isOpen, isSuperAdmin, companies.length]);

    /** Firma değişince kullanıcı seçimi sıfırlanır ve o firmanın kullanıcıları yüklenir. */
    const handleCompanySelect = async (companyId) => {
        setOnBehalf({ companyId, userId: "" });
        setCompanyUsers([]);
        if (!companyId) return;
        try {
            const { data } = await axios.get(API.admin_users_list,
                { params: { companyId, size: USER_OPTIONS_LIMIT } });
            setCompanyUsers(data?.data?.content || []);
        } catch {
            toast.error("Firma kullanıcıları yüklenemedi");
        }
    };

    /** Kapanış: adına açma seçimleri bir sonraki talebe sızmamalı. */
    const closeModal = () => {
        setOnBehalf({ companyId: "", userId: "" });
        setCompanyUsers([]);
        toggle();
    };

    const submitCreate = async () => {
        if (!form.subject.trim() || !form.message.trim()) {
            toast.error("Konu ve mesaj zorunludur");
            return;
        }
        setSaving(true);
        try {
            // Kullanıcı seçiliyse talep onun adına açılır; seçim yoksa gövde eskisiyle aynı kalır.
            const values = (isSuperAdmin && onBehalf.userId)
                ? { ...form, onBehalfOfUserId: onBehalf.userId }
                : form;
            const { data } = await axios.post(
                API.tickets_list,
                buildMessagePayload(values, attachments.files, attachments.pastedImages));
            toast.success(values.onBehalfOfUserId
                ? "Destek talebi kullanıcı adına oluşturuldu"
                : "Destek talebiniz oluşturuldu");
            closeModal();
            setForm(DEFAULT_FORM);
            attachments.clear();
            onCreated?.(data?.data?.id);
        } catch (err) {
            // 429: talep açma limiti. Form kapanmaz, yazılanlar korunur; kullanıcı
            // süre dolduktan sonra aynı içerikle tekrar gönderebilsin.
            if (err.response?.status === TOO_MANY_REQUESTS) {
                toast.warn(err.response?.data?.message
                    || "Çok sık destek talebi oluşturuyorsunuz. Lütfen biraz bekleyin.");
            } else {
                toast.error(err.response?.data?.message || "Talep oluşturulamadı");
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={closeModal} centered>
            <ModalHeader toggle={closeModal}>Yeni Destek Talebi</ModalHeader>
            <ModalBody>
                {isSuperAdmin && (
                    <>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Firma</Label>
                                    <Input type="select" value={onBehalf.companyId} disabled={saving}
                                        onChange={(e) => handleCompanySelect(e.target.value)}>
                                        <option value="">Firma seçin</option>
                                        {companies.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Talep Sahibi</Label>
                                    <Input type="select" value={onBehalf.userId}
                                        disabled={saving || !onBehalf.companyId}
                                        onChange={(e) => setOnBehalf({ ...onBehalf, userId: e.target.value })}>
                                        <option value="">{onBehalf.companyId ? "Kullanıcı seçin" : "Önce firma seçin"}</option>
                                        {companyUsers.map((u) => (
                                            <option key={u.id} value={u.id}>{u.username} — {u.email}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                        <p className="text-muted small">
                            Telefon veya başka bir kanaldan iletilen talepler içindir: talep seçilen
                            kullanıcının adına açılır, kendi "Destek Taleplerim" ekranında görünür ve
                            bilgilendirme maili alır. Boş bırakılırsa talep sizin adınıza açılır.
                        </p>
                    </>
                )}
                <FormGroup>
                    <Label>Konu <span className="text-danger">*</span></Label>
                    <Input value={form.subject} maxLength={200}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Kısa bir konu başlığı" />
                </FormGroup>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Kategori</Label>
                            <Input type="select" value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{CATEGORY_META[c]}</option>)}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Öncelik</Label>
                            <Input type="select" value={form.priority}
                                onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
                            </Input>
                        </FormGroup>
                    </Col>
                </Row>
                <FormGroup>
                    <Label>Mesaj <span className="text-danger">*</span></Label>
                    <Input type="textarea" rows={5} value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        onPaste={attachments.handlePaste}
                        placeholder="Talebinizi detaylı açıklayın" />
                </FormGroup>
                <FormGroup>
                    <AttachmentPicker
                        items={attachments.items}
                        onAdd={attachments.add}
                        onRemove={attachments.remove}
                        disabled={saving}
                    />
                </FormGroup>
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={closeModal} disabled={saving}>Vazgeç</Button>
                <Button style={{ background: NAVY, borderColor: NAVY }} onClick={submitCreate} disabled={saving}>
                    {saving ? <Spinner size="sm" /> : "Gönder"}
                </Button>
            </ModalFooter>
        </Modal>
    );
}
